import { useCallback, useState } from 'react';

import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import { interactionHistoryId } from '@poseidon/components/ContactDetail';
import { Button } from '@poseidon/components/Input2';

import { summarizeHistoryEntry } from '~/components/Sequence/sequenceHistory';
import { SequenceHistoryCard } from '~/components/Sequence/SequenceHistoryCard';
import { SequenceHistoryPopover } from '~/components/Sequence/SequenceHistoryPopover';

/** How many of the newest entries show before "Show earlier" — the canvas opens on NOW, just below these. */
export const RECENT_HISTORY_COUNT = 6;
const HEADER_H = 44;
const ROW_H = 62;
const MAX_CARD_W = 240;

interface Props {
  /** The contact's history, oldest first (the order the feed shows it). */
  items: HistoryItem[];
  actions: InteractionHistoryActions;
  /** The canvas spine's x and the content width, so the cards sit on the same line as the sequence. */
  spineX: number;
  width: number;
}

/**
 * The contact's history at the top of the sequence canvas — the past flowing down into the sequence: a compact
 * card per entry, centred on the spine like the graph's own pills, newest last (right above Prep). The newest
 * {@link RECENT_HISTORY_COUNT} show; the rest fold behind "Show earlier" so a long history doesn't stretch the
 * canvas out of reach. A card opens the full entry in a popover.
 */
export function SequenceHistoryColumn({ items, actions, spineX, width }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [opened, setOpened] = useState<{ id: string; anchor: Element } | null>(null);
  const shown = showAll ? items : items.slice(-RECENT_HISTORY_COUNT);
  const folded = items.length - shown.length;
  // Centred on the spine, so no wider than twice the room to its left.
  const cardWidth = Math.max(180, Math.min(MAX_CARD_W, spineX * 2 - 16, width - 16));
  const openedEntry = opened == null ? undefined : items.find((e) => interactionHistoryId(e) === opened.id);
  const close = useCallback(() => setOpened(null), []);

  return (
    <div data-history-column className="relative" style={{ width, height: HEADER_H + shown.length * ROW_H }}>
      {/* The spine through the past: from the first card down to the canvas below, where it meets Prep. */}
      <span
        aria-hidden
        className="pointer-events-none absolute w-0.5 bg-lynch-300 dark:bg-slate-600"
        style={{ left: spineX - 1, top: HEADER_H + ROW_H / 2, bottom: 0 }}
      />
      <div
        data-no-pan
        className="absolute flex items-center gap-2 whitespace-nowrap"
        style={{ left: spineX, top: 10, transform: 'translate(-50%, 0)' }}>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
          Activity
        </span>
        {folded > 0 && (
          <Button size="2xs" text onClick={() => setShowAll(true)}>
            Show {folded} earlier
          </Button>
        )}
        {showAll && items.length > RECENT_HISTORY_COUNT && (
          <Button size="2xs" text onClick={() => setShowAll(false)}>
            Show fewer
          </Button>
        )}
      </div>
      {shown.map((entry, k) => {
        const id = interactionHistoryId(entry);
        return (
          <SequenceHistoryCard
            key={id}
            entryId={id}
            summary={summarizeHistoryEntry(entry, actions.currentUserId)}
            top={HEADER_H + k * ROW_H + ROW_H / 2}
            spineX={spineX}
            cardWidth={cardWidth}
            open={opened?.id === id}
            onOpen={(entryId, anchor) => setOpened({ id: entryId, anchor })}
          />
        );
      })}
      {opened != null && openedEntry != null && (
        <SequenceHistoryPopover entry={openedEntry} actions={actions} anchor={opened.anchor} onClose={close} />
      )}
    </div>
  );
}
